import { useCallback, useEffect, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import Header from '@/components/Header';
import Footer from '@/components/Footer';
import UploadState from '@/components/UploadState';
import ProcessingState from '@/components/ProcessingState';
import ResultsState from '@/components/ResultsState';
import { getResult, submitCustomerDetails, uploadBill, warmUpApi } from '@/lib/api';
import { useAnalysisJob } from '@/hooks/useAnalysisJob';

const STEP = Object.freeze({
  UPLOAD: 'upload',
  PROCESSING: 'processing',
  RESULTS: 'results',
});

export default function AnalyzerPage() {
  const [searchParams] = useSearchParams();
  const initialJobId = searchParams.get('job_id');
  const [step, setStep] = useState(STEP.UPLOAD);
  const [jobId, setJobId] = useState(null);
  const [customerSubmitted, setCustomerSubmitted] = useState(false);
  const [directResult, setDirectResult] = useState(null);

  const { status, result, error, startPolling, resetJob } = useAnalysisJob();

  // Start the API waking while the customer is still choosing a file, so a
  // sleeping free-tier instance is not 30-60s of their analysis time.
  useEffect(() => {
    warmUpApi();
  }, []);

  // Direct load a job by ?job_id= (for admin sharing / preview)
  useEffect(() => {
    if (!initialJobId) return;
    (async () => {
      try {
        const r = await getResult(initialJobId);
        if (r && r.status === 'ready') {
          setDirectResult(r);
          setStep(STEP.RESULTS);
        } else {
          toast.error('That analysis isn\u2019t ready yet.');
        }
      } catch {
        toast.error('Could not load that analysis.');
      }
    })();
  }, [initialJobId]);

  useEffect(() => {
    if (step === STEP.PROCESSING && result && customerSubmitted) {
      setStep(STEP.RESULTS);
    }
  }, [step, result, customerSubmitted]);

  const handleUpload = useCallback(
    async ({ file }) => {
      resetJob();
      setDirectResult(null);
      setCustomerSubmitted(false);
      try {
        const res = await uploadBill({ file });
        setJobId(res.job_id);
        setStep(STEP.PROCESSING);
        startPolling(res.job_id);
        toast.success('Bill uploaded. Analyzing\u2026');
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Upload failed');
        throw e;
      }
    },
    [resetJob, startPolling]
  );

  const handleCustomerSubmit = useCallback(
    async (payload) => {
      if (!jobId) throw new Error('No job id');
      await submitCustomerDetails({ job_id: jobId, ...payload });
      setCustomerSubmitted(true);
    },
    [jobId]
  );

  const reset = useCallback(() => {
    resetJob();
    setDirectResult(null);
    setJobId(null);
    setCustomerSubmitted(false);
    setStep(STEP.UPLOAD);
  }, [resetJob]);

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main className="flex-1">
        <AnimatePresence mode="wait">
          {step === STEP.UPLOAD && (
            <UploadState key="upload" onSubmit={handleUpload} />
          )}
          {step === STEP.PROCESSING && (
            <ProcessingState
              key="processing"
              status={status}
              submitted={customerSubmitted}
              onSubmitCustomer={handleCustomerSubmit}
              error={error}
            />
          )}
          {step === STEP.RESULTS && (
            <ResultsState key="results" submission={directResult || result} onReset={reset} />
          )}
        </AnimatePresence>
      </main>
      <Footer />
    </div>
  );
}
